/**
 * Inspector's checks on a proposed fix as one clickable checklist (ported
 * from the findings redesign prototype): a checkbox per check, "x of y" and a
 * progress bar, and a fill field (text, date or a person) for each `{{key}}`
 * fill-in. Every tick records who and when; ticking an approval or a caveat
 * asks first (⌘/Ctrl-click skips the dialog). Ticks are optimistic and roll
 * back with a toast when the server refuses them.
 *
 * The checklist never blocks Apply: it is the reviewer's record, not a gate.
 *
 * @module components/regulation/finding/changes/FindingFixChecklist
 */
import {
  FIX_FILL_MAX_LENGTH,
  fixChecklistProgress,
  isFixCheckDone,
  typedFixCheck,
  type DocumentChangesView,
  type TypedFixCheck,
} from "@repo/shared/findingChanges";
import { ListTodoIcon, PenLineIcon, ShieldCheckIcon, StampIcon, TriangleAlertIcon } from "lucide-react";
import { useCallback, useRef, useState, type ReactNode } from "react";

import { cn } from "~/lib/utils";
import type { OrganizationPerson } from "../../../../state/governance";
import { useFixReview } from "../../../../state/findingFixReview";
import { normalizeFillValue } from "../../../../state/findingFixReview.logic";
import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
} from "../../../ui/alert-dialog";
import { Button } from "../../../ui/button";
import { Checkbox } from "../../../ui/checkbox";
import { Input } from "../../../ui/input";
import { Kbd } from "../../../ui/kbd";
import { toastManager } from "../../../ui/toast";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../../../ui/tooltip";
import { Hint, PersonFace, personLabel } from "../findingGlyphs";
import { Picker } from "../Picker";
import {
  askBeforeTicking,
  confirmCheckCopy,
  confirmationDetail,
  confirmationStamp,
  namedConfirmation,
  fixCheckLabel,
  progressPercent,
  untickWrites,
} from "./FindingFixChecklist.logic";

const CHECK_ICON: Readonly<Record<TypedFixCheck["kind"], ReactNode>> = {
  fill: <PenLineIcon />,
  confirm: <ShieldCheckIcon />,
  approval: <StampIcon />,
  followup: <ListTodoIcon />,
  caveat: <TriangleAlertIcon />,
};

const MOD_KEY = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform) ? "⌘" : "Ctrl";

/** The danger dialog in front of an important tick. */
function ConfirmCheckDialog({
  check,
  onConfirm,
  onClose,
}: {
  readonly check: TypedFixCheck | null;
  readonly onConfirm: () => void;
  readonly onClose: () => void;
}) {
  const copy = check ? confirmCheckCopy(check) : null;
  return (
    <AlertDialog open={check !== null} onOpenChange={(open) => !open && onClose()}>
      <AlertDialogPopup data-confirm-check="">
        <AlertDialogHeader>
          <AlertDialogTitle>{copy?.title}</AlertDialogTitle>
          <AlertDialogDescription>{copy?.description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="items-center">
          <p className="me-auto flex items-center gap-0.5 text-xs text-muted-foreground max-sm:hidden">
            <Kbd>{MOD_KEY}</Kbd>-click the checkbox to skip this
          </p>
          <AlertDialogClose render={<Button variant="outline" />}>Cancel</AlertDialogClose>
          <Button variant="destructive" onClick={onConfirm} data-confirm-check-submit="">
            {copy?.action}
          </Button>
        </AlertDialogFooter>
      </AlertDialogPopup>
    </AlertDialog>
  );
}

/** A text or date fill-in: saved when it loses focus or on Enter. */
function FillInput({
  check,
  value,
  disabled,
  onSave,
}: {
  readonly check: TypedFixCheck & { readonly field: NonNullable<TypedFixCheck["field"]> };
  readonly value: string;
  readonly disabled: boolean;
  readonly onSave: (value: string) => void;
}) {
  return (
    <Input
      nativeInput
      size="sm"
      // Remount when the stored value changes (a rollback, another reviewer), so the field shows it.
      key={value}
      type={check.field.input === "date" ? "date" : "text"}
      defaultValue={value}
      disabled={disabled}
      maxLength={FIX_FILL_MAX_LENGTH}
      aria-label={check.field.label}
      onBlur={(event) => {
        if (normalizeFillValue(event.target.value) !== value) onSave(event.target.value);
      }}
      onKeyDown={(event) => {
        if (event.key === "Enter") (event.target as HTMLInputElement).blur();
        if (event.key === "Escape") {
          (event.target as HTMLInputElement).value = value;
          (event.target as HTMLInputElement).blur();
        }
      }}
      placeholder={check.field.placeholder ?? "Optional"}
      className="w-28 shrink-0 text-[13px] sm:w-44 sm:text-[13px]"
      data-fix-fill={check.field.key}
    />
  );
}

export interface FindingFixChecklistProps {
  readonly organizationId: string;
  /** The proposal on screen; its checks and its finding or suggestion address the record. */
  readonly proposal: DocumentChangesView;
  /** The organisation's people, for person fill-ins. */
  readonly people: ReadonlyArray<OrganizationPerson>;
  /** An admin on an open finding; otherwise the checklist is read-only. */
  readonly editable: boolean;
  /** Shown beside an optimistic tick until the server's record arrives. */
  readonly actorName?: string | null;
  readonly className?: string;
}

/** Inspector's checks as one checklist. Nothing here blocks Apply. */
export function FindingFixChecklist({
  organizationId,
  proposal,
  people,
  editable,
  actorName = null,
  className,
}: FindingFixChecklistProps) {
  const onError = useCallback((message: string) => {
    toastManager.add({ type: "error", title: "Checklist not updated", description: message });
  }, []);
  const { review, confirm, fill } = useFixReview(organizationId, proposal, { actorName, onError });
  const [asking, setAsking] = useState<number | null>(null);
  // Set by the click or key press that is about to toggle a box: ⌘/Ctrl skips the dialog.
  const skipDialog = useRef(false);
  const checks = proposal.outstandingChecks;
  if (checks.length === 0) return null;
  const typed = checks.map(typedFixCheck);
  const { done, total } = fixChecklistProgress(checks, review);
  const fills = review?.fills ?? {};
  return (
    <section
      aria-label="Checklist"
      className={cn("overflow-hidden rounded-lg border border-border/70 bg-card", className)}
      data-finding-fix-checklist=""
    >
      <header className="flex h-12 items-center gap-3 border-b border-border/70 px-4">
        <h3 className="text-[13px] font-medium">Checklist</h3>
        <span className="text-[13px] tabular-nums text-muted-foreground" data-checklist-count="">
          {done} of {total}
        </span>
        <span
          className="ms-auto h-1 w-24 overflow-hidden rounded-full bg-border"
          role="progressbar"
          aria-label="Checklist progress"
          aria-valuemin={0}
          aria-valuemax={total}
          aria-valuenow={done}
        >
          <span
            className="block h-full rounded-full bg-emerald-500 transition-[width] duration-200"
            style={{ width: `${progressPercent(done, total)}%` }}
          />
        </span>
      </header>
      <ul className="divide-y divide-border/60">
        {typed.map((check, index) => {
          const checked = isFixCheckDone(checks[index]!, index, review);
          const stamped = review?.confirmations[String(index)];
          const confirmation = stamped ? namedConfirmation(stamped, people) : undefined;
          const label = fixCheckLabel(check);
          const ask = askBeforeTicking(check) && !checked && editable;
          const field = check.kind === "fill" ? check.field : undefined;
          const box = (
            <Checkbox
              className="size-4 sm:size-4"
              checked={checked}
              disabled={!editable}
              aria-label={label}
              onClickCapture={(event) => {
                skipDialog.current = event.metaKey || event.ctrlKey;
              }}
              onKeyDownCapture={(event) => {
                skipDialog.current = event.metaKey || event.ctrlKey;
              }}
              onCheckedChange={(value) => {
                const skip = skipDialog.current;
                skipDialog.current = false;
                if (value === true) {
                  if (askBeforeTicking(check) && !skip) setAsking(index);
                  else void confirm(index, true);
                  return;
                }
                // Unticking clears the confirmation and a fill-in's value alike.
                for (const write of untickWrites(check, index, review)) {
                  void (write.action === "confirm" ? confirm(write.index, write.confirmed) : fill(write.key, write.value));
                }
              }}
            />
          );
          return (
            <li key={`${check.kind}-${index}`} className="flex min-h-11 items-center gap-3 px-4 py-1.5" data-check-kind={check.kind}>
              {ask ? (
                <Tooltip>
                  <TooltipTrigger render={box} />
                  <TooltipPopup>
                    {check.kind === "approval" ? "Record approval" : "Acknowledge"} · <Kbd className="mx-0.5">{MOD_KEY}</Kbd>-click to skip
                    the dialog
                  </TooltipPopup>
                </Tooltip>
              ) : (
                box
              )}
              <span
                className={cn(
                  "flex size-4 shrink-0 items-center justify-center text-muted-foreground [&_svg]:size-4",
                  check.kind === "caveat" && "text-amber-600 dark:text-amber-400",
                  check.kind === "approval" && "text-violet-600 dark:text-violet-400",
                )}
                aria-hidden
              >
                {CHECK_ICON[check.kind]}
              </span>
              <Hint
                content={
                  <>
                    <p className="text-[13px] leading-6 text-foreground">{check.text}</p>
                    {field ? (
                      <p className="mt-1">
                        Written into the proposed text where it says <code>{`{{${field.key}}}`}</code>.
                      </p>
                    ) : null}
                    {confirmation ? <p className="mt-1">{confirmationDetail(confirmation)}</p> : null}
                  </>
                }
                render={<span className={cn("min-w-0 flex-1 truncate text-[13px]", checked && "text-muted-foreground")} />}
              >
                {label}
              </Hint>
              {field ? (
                field.input === "person" ? (
                  <Picker
                    label={field.label}
                    disabled={!editable}
                    align="end"
                    options={people.map((person) => ({
                      value: personLabel(person),
                      label: person.jobTitle ? `${personLabel(person)} · ${person.jobTitle}` : personLabel(person),
                      icon: <PersonFace person={person} className="size-4 text-[8px]" />,
                      checked: fills[field.key] === personLabel(person),
                    }))}
                    onPick={(value) => void fill(field.key, value === fills[field.key] ? "" : value)}
                    triggerClassName={cn("h-7 border border-dashed border-border text-[13px]", fills[field.key] && "border-solid")}
                    trigger={
                      fills[field.key] ? (
                        <span className="flex min-w-0 items-center gap-1.5">
                          {(() => {
                            const person = people.find((candidate) => personLabel(candidate) === fills[field.key]);
                            return person ? <PersonFace person={person} className="size-4 text-[8px]" /> : null;
                          })()}
                          <span className="truncate">{fills[field.key]}</span>
                        </span>
                      ) : (
                        <span className="text-muted-foreground">{field.placeholder ?? "Optional"}</span>
                      )
                    }
                  />
                ) : (
                  <FillInput
                    check={{ ...check, field }}
                    value={fills[field.key] ?? ""}
                    disabled={!editable}
                    onSave={(value) => void fill(field.key, value)}
                  />
                )
              ) : confirmation ? (
                <span className="shrink-0 text-[13px] tabular-nums text-muted-foreground" data-check-stamp="">
                  {confirmationStamp(confirmation)}
                </span>
              ) : null}
            </li>
          );
        })}
      </ul>
      <ConfirmCheckDialog
        check={asking === null ? null : (typed[asking] ?? null)}
        onClose={() => setAsking(null)}
        onConfirm={() => {
          if (asking !== null) void confirm(asking, true);
          setAsking(null);
        }}
      />
    </section>
  );
}
